import { useState, useEffect, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';

interface Message {
  id: string;
  content: string;
  timestamp: string;
  isOutgoing: boolean;
  channel: 'email' | 'sms' | 'phone';
  status: 'sent' | 'delivered' | 'read' | 'failed';
  attachments?: File[];
  metadata?: any;
}

interface Conversation {
  id: string;
  contact: {
    id: string;
    name: string;
    email: string;
    company?: string;
    avatar?: string;
  };
  lastMessage: Message;
  status: 'active' | 'positive' | 'priority' | 'needs-response' | 'resolved';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  campaign?: string;
  messageCount: number;
  unreadCount: number;
  labels: string[];
  isStarred: boolean;
  updatedAt: string;
}

interface RealTimeUpdate {
  type: 'new_message' | 'message_status_update' | 'conversation_update';
  conversationId: string;
  data: any;
}

interface ConnectionStatus {
  connected: boolean;
  reconnecting: boolean;
  lastPing?: number;
  reconnectAttempts: number;
}

interface UseRealTimeConversationsProps {
  onNewMessage?: (conversationId: string, message: Message) => void;
  onMessageStatusUpdate?: (conversationId: string, messageId: string, status: Message['status']) => void;
  onConversationUpdate?: (conversation: Partial<Conversation>) => void;
  onConversationListUpdate?: () => void;
}

interface UseRealTimeConversationsReturn {
  connectionStatus: ConnectionStatus;
  subscribe: (conversationId: string) => void;
  unsubscribe: (conversationId: string) => void;
  unsubscribeAll: () => void;
}

export function useRealTimeConversations({
  onNewMessage,
  onMessageStatusUpdate,
  onConversationUpdate,
  onConversationListUpdate
}: UseRealTimeConversationsProps = {}): UseRealTimeConversationsReturn {
  
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>({
    connected: false,
    reconnecting: false,
    reconnectAttempts: 0
  });

  const socketRef = useRef<Socket | null>(null);
  const subscribedConversationsRef = useRef<Set<string>>(new Set());
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const heartbeatIntervalRef = useRef<NodeJS.Timeout>();

  // Initialize WebSocket connection
  const connect = useCallback(() => {
    if (socketRef.current?.connected) {
      return;
    }

    setConnectionStatus(prev => ({ ...prev, reconnecting: true }));

    try {
      const wsUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
      
      socketRef.current = io(wsUrl, {
        path: '/api/conversations/socket',
        transports: ['websocket', 'polling'],
        timeout: 10000,
        forceNew: true,
        auth: {
          token: localStorage.getItem('token')
        }
      });

      const socket = socketRef.current;

      // Connection event handlers
      socket.on('connect', () => {
        console.log('✅ Real-time conversations connected');
        setConnectionStatus({
          connected: true,
          reconnecting: false,
          reconnectAttempts: 0
        });

        // Re-subscribe to all previously subscribed conversations
        subscribedConversationsRef.current.forEach(conversationId => {
          socket.emit('join_conversation', conversationId);
        });

        // Start heartbeat
        startHeartbeat();
      });

      socket.on('disconnect', (reason) => {
        console.log('❌ Real-time conversations disconnected:', reason);
        setConnectionStatus(prev => ({ 
          ...prev, 
          connected: false, 
          reconnecting: false 
        }));
        stopHeartbeat();

        // Auto-reconnect unless disconnected by client
        if (reason !== 'io client disconnect') {
          scheduleReconnect();
        }
      });

      socket.on('connect_error', (error) => {
        console.error('🔌 WebSocket connection error:', error);
        setConnectionStatus(prev => ({ 
          ...prev, 
          connected: false, 
          reconnecting: false,
          reconnectAttempts: prev.reconnectAttempts + 1
        }));
        scheduleReconnect();
      });

      // Message event handlers
      socket.on('new_message', (data: { conversationId: string; message: Message }) => {
        console.log('📨 New message received:', data);
        onNewMessage?.(data.conversationId, data.message);
        onConversationListUpdate?.();
      });

      socket.on('message_status_update', (data: { 
        conversationId: string; 
        messageId: string; 
        status: Message['status'] 
      }) => {
        console.log('📋 Message status updated:', data);
        onMessageStatusUpdate?.(data.conversationId, data.messageId, data.status);
      });

      socket.on('conversation_update', (data: Partial<Conversation>) => {
        console.log('💬 Conversation updated:', data);
        onConversationUpdate?.(data);
        onConversationListUpdate?.();
      });

      // Automation event handlers
      socket.on('automation_response_sent', (data: { conversationId: string; message: Message }) => {
        console.log('🤖 Automation response sent:', data);
        onNewMessage?.(data.conversationId, data.message);
        onConversationListUpdate?.();
      });

      socket.on('automation_response_queued', (data: { conversationId: string }) => {
        console.log('⏰ Automation response queued:', data);
        // This could trigger a queued responses refresh
      });

      // Heartbeat response
      socket.on('pong', (data: { timestamp: number }) => {
        const latency = Date.now() - data.timestamp;
        setConnectionStatus(prev => ({ 
          ...prev, 
          lastPing: latency 
        }));
      });

    } catch (error) {
      console.error('❌ Failed to initialize WebSocket:', error);
      setConnectionStatus(prev => ({ 
        ...prev, 
        connected: false, 
        reconnecting: false 
      }));
      scheduleReconnect();
    }
  }, [onNewMessage, onMessageStatusUpdate, onConversationUpdate, onConversationListUpdate]);

  // Disconnect WebSocket
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    stopHeartbeat();
    
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    
    setConnectionStatus({
      connected: false,
      reconnecting: false,
      reconnectAttempts: 0
    });
  }, []);

  // Schedule reconnection with exponential backoff
  const scheduleReconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    const delay = Math.min(1000 * Math.pow(2, connectionStatus.reconnectAttempts), 30000);
    console.log(`🔄 Scheduling reconnect in ${delay}ms`);

    reconnectTimeoutRef.current = setTimeout(() => {
      if (!socketRef.current?.connected) {
        connect();
      }
    }, delay);
  }, [connectionStatus.reconnectAttempts, connect]);

  // Start heartbeat to monitor connection
  const startHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }

    heartbeatIntervalRef.current = setInterval(() => {
      if (socketRef.current?.connected) {
        socketRef.current.emit('ping', { timestamp: Date.now() });
      }
    }, 30000); // Every 30 seconds
  }, []);

  // Stop heartbeat
  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = undefined;
    }
  }, []);

  // Subscribe to a conversation for real-time updates
  const subscribe = useCallback((conversationId: string) => {
    if (!conversationId) return;

    subscribedConversationsRef.current.add(conversationId);
    
    if (socketRef.current?.connected) {
      socketRef.current.emit('join_conversation', conversationId);
      console.log(`🔔 Subscribed to conversation: ${conversationId}`);
    }
  }, []);

  // Unsubscribe from a conversation
  const unsubscribe = useCallback((conversationId: string) => {
    if (!conversationId) return;

    subscribedConversationsRef.current.delete(conversationId);
    
    if (socketRef.current?.connected) {
      socketRef.current.emit('leave_conversation', conversationId);
      console.log(`🔕 Unsubscribed from conversation: ${conversationId}`);
    }
  }, []);

  // Unsubscribe from all conversations
  const unsubscribeAll = useCallback(() => {
    subscribedConversationsRef.current.forEach(conversationId => {
      if (socketRef.current?.connected) {
        socketRef.current.emit('leave_conversation', conversationId);
      }
    });
    
    subscribedConversationsRef.current.clear();
    console.log('🔕 Unsubscribed from all conversations');
  }, []);

  // Initialize connection on mount
  useEffect(() => {
    connect();

    // Cleanup on unmount
    return () => {
      unsubscribeAll();
      disconnect();
    };
  }, [connect, disconnect, unsubscribeAll]);

  // Retry connection if it drops
  useEffect(() => {
    if (!connectionStatus.connected && !connectionStatus.reconnecting && connectionStatus.reconnectAttempts < 5) {
      connect();
    }
  }, [connectionStatus.connected, connectionStatus.reconnecting, connectionStatus.reconnectAttempts, connect]);

  return {
    connectionStatus,
    subscribe,
    unsubscribe,
    unsubscribeAll
  };
} 